import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js'
import axios from 'axios'
import {
  Camera,
  Check,
  CheckCheck,
  HelpCircle,
  MessageSquareHeart,
  Search,
  SlidersHorizontal,
  XIcon,
} from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import Stripe from 'stripe'
import Button from '~/src/components/Button'
import CheckoutButton from '~/src/components/stripe/CheckoutButton'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { Label } from '~/src/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/src/components/ui/popover'
import { Switch } from '~/src/components/ui/switch'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import { currencyFormatter } from '~/src/lib/helpers'
import logger from '~/src/lib/logger'
import { STRIPE_PUBLISHABLE_KEY } from '~/src/lib/stripe'
import {
  ACTIVE_PLANS,
  calculateYearlySavingsPercentage,
  getPriceInCents,
  getTrialDays,
} from '~/src/lib/stripe/planPricing'
import {
  OnboardingStep,
  useOnboardingActions,
  usePaymentStatus,
} from '~/src/stores/useOnboardingStore'

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY as string)

interface Feature {
  name: string
  description?: string
  tooltip?: string
  free: boolean
  pro: boolean
  icon: React.ReactNode
}

const features: Feature[] = [
  {
    name: 'Basic search',
    description: 'Simple similarity search, loved by millions',
    free: true,
    pro: true,
    icon: <Search className='h-4 w-4 md:h-5 md:w-5' />,
  },
  {
    name: 'Search by photo',
    description: 'Upload an image or use your camera to find similar products',
    free: false,
    pro: true,
    icon: <Camera className='h-4 w-4 md:h-5 md:w-5' />,
  },
  {
    name: 'Advanced filtering',
    description: 'Even more powerful ways to get the look you want for less',
    free: false,
    pro: true,
    icon: <SlidersHorizontal className='h-4 w-4 md:h-5 md:w-5' />,
  },
  {
    name: 'Expert human shopper',
    description:
      'Our team of expert deal-finders work to find you deals while you sleep',
    free: false,
    pro: true,
    icon: <MessageSquareHeart className='h-4 w-4 md:h-5 md:w-5' />,
  },
  // {
  //   name: 'Factory Direct',
  //   description: 'Up to 75% off exclusive deals, direct from manufacturers',
  //   tooltip:
  //     'Dupe.com has partnered with top furniture manufacturers to offer showroom-quality pieces at lower than wholesale prices.',
  //   free: false,
  //   pro: true,
  //   icon: <Factory className='h-4 w-4 md:h-5 md:w-5' />,
  // },
]

export interface PaywallContentProps {
  onCancel?: () => void
  onSuccess?: () => void
  source?: string
  featureName?: string
}

const PaywallContent: React.FC<PaywallContentProps> = ({
  onCancel,
  onSuccess,
  source = 'paywall',
  featureName,
}) => {
  const router = useRouter()
  const analytics = useAnalytics()
  const { authUser } = useAuthUser()
  const searchParams = useSearchParams()

  const { userData, updateUserDetails } = useAuthContext()

  const [applePaySupported, setApplePaySupported] = useState<
    boolean | undefined
  >(undefined)

  const [isYearly, setIsYearly] = useState(true)
  const [processing, setProcessing] = useState(false)

  const stripeSessionIdParam = searchParams?.get('session_id') // check if we have a session id from stripe
  const statusParam = searchParams?.get('status')
  const planParam = searchParams?.get('plan')

  useEffect(() => {
    if (!stripeSessionIdParam || !statusParam) return
    if (statusParam === 'success') return
    if (planParam === 'yearly') {
      setIsYearly(true)
    } else {
      setIsYearly(false)
    }
  }, [stripeSessionIdParam, statusParam, planParam])

  const { featureOn: paywallStripePayFeatureOn } = useIsFeatureOn(
    FeatureFlags.PaywallStripePay,
    true
  )

  const paymentStatus = usePaymentStatus()

  const stripeSubscriptionId = userData?.metadata?.stripeSubscriptionId
  const existingSubscription = userData?.metadata?.subscription as {
    billingPeriod: string
    paymentStatus: string
    plan: string
  } | null
  const existingBillingPeriod = existingSubscription?.billingPeriod

  const onboardingActions = useOnboardingActions()

  const planId = isYearly ? ACTIVE_PLANS.yearly : ACTIVE_PLANS.monthly
  const priceInCents = getPriceInCents(planId)
  const freeTrialDays = getTrialDays(planId)

  const currency = 'USD'
  const calculatedSavings = calculateYearlySavingsPercentage()
  const savings = `${calculatedSavings}%`
  const yearlyPriceInCents = getPriceInCents(ACTIVE_PLANS.yearly)
  const monthlyPriceInCents = getPriceInCents(ACTIVE_PLANS.monthly)

  // get amount in cents - has to be the same as the backend for plan ids.
  const AMOUNT_IN_CENTS = isYearly ? yearlyPriceInCents : monthlyPriceInCents

  const options: StripeElementsOptions = {
    mode: 'subscription',
    amount: AMOUNT_IN_CENTS,
    currency: 'usd',
  }

  const handleSelectPlan = useCallback(
    async (plan: 'free' | 'pro', callbackData?: Stripe.Subscription) => {
      setProcessing(true)

      let stripeSessionData = null as { url?: string } | null

      if (
        paywallStripePayFeatureOn &&
        plan !== 'free' &&
        !stripeSubscriptionId &&
        !callbackData
      ) {
        // const currentUrl = window.location.href
        // make api call to generate checkout session link
        try {
          const resp = await axios.post(
            '/api/stripe/create-checkout-session',
            {
              plan: isYearly ? 'yearly' : 'monthly',
              email: authUser?.email,
              params: {
                onboarding: 1,
              },
            },
            {
              headers: {
                'x-authorization':
                  authUser?.accessToken || (await authUser?.getIdToken()),
              },
            }
          )

          logger.debug('Stripe checkout session response:', { resp })
          stripeSessionData = resp.data as { url?: string }
        } catch (error) {
          logger.error('Error creating stripe checkout session:', { error })
          toast.error('Error creating checkout session')
          setProcessing(false)
          return
        }
      }

      const billingPeriod = isYearly ? 'yearly' : 'monthly'

      // Track analytics
      analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
        step: 'paywall',
        plan,
        billingPeriod,
        paymentStatus,
        source,
        featureName,
      })

      // If we're in the onboarding flow, save to store
      onboardingActions.setStepData({
        subscription: {
          plan: existingSubscription?.plan || plan,
          billingPeriod: existingSubscription?.billingPeriod || billingPeriod,
          ...(plan === 'pro' && {
            paymentStatus,
          }),
        },
      })

      // Update user details in the database to show free user plan in metadata
      if (plan === 'free') {
        await updateUserDetails({
          subscription: {
            plan: 'free',
          },
        })
      }

      if (!!callbackData) {
        await updateUserDetails({
          subscription: {
            plan: 'pro',
            billingPeriod: isYearly ? 'yearly' : 'monthly',
            paymentStatus: 'paid',
            liveMode: callbackData.livemode,
          },
          ...(callbackData.customer && {
            stripeCustomerId: callbackData.customer as string,
          }),
          ...(callbackData.id && {
            stripeSubscriptionId: callbackData.id as string,
          }),
        })

        // Ensure we also call validate session here when using express checkout (Apple Pay)
        analytics.track(AnalyticsEventName.AuthModalOnboardingPaywallValidate, {
          success: true,
          step: 'paywall',
          plan: 'pro',
          billingPeriod: isYearly ? 'yearly' : 'monthly',
          customerId: callbackData.customer as string,
          sessionId: callbackData.id,
          expressCheckout: true,
        })
      }

      if (stripeSessionData?.url) {
        logger.debug('Redirecting to stripe session:', {
          url: stripeSessionData.url,
        })

        router.push(stripeSessionData.url)
        return
      }

      setProcessing(false)

      // If plan is "Free" or used Express Checkout (apple pay) or already has a subscription we will get here
      onboardingActions.markStepComplete(OnboardingStep.PAYWALL)
      onboardingActions.nextStep(OnboardingStep.COMPLETE)

      if (onSuccess) {
        onSuccess()
      }
    },
    [
      paywallStripePayFeatureOn,
      stripeSubscriptionId,
      isYearly,
      analytics,
      paymentStatus,
      source,
      featureName,
      onboardingActions,
      existingSubscription?.plan,
      existingSubscription?.billingPeriod,
      onSuccess,
      authUser,
      router,
      updateUserDetails,
    ]
  )

  // Initialize isYearly based on user's existing subscription
  useEffect(() => {
    if (existingBillingPeriod) {
      setIsYearly(existingBillingPeriod === 'yearly')
    }
  }, [existingBillingPeriod])

  const FeatureRow: React.FC<{ feature: Feature }> = ({ feature }) => (
    <div className='grid grid-cols-12 gap-2 px-3 py-1.5 text-left md:gap-4 md:py-2'>
      <div className='col-span-8 md:col-span-6'>
        <div className='flex gap-3'>
          <span className='mt-1'>{feature.icon}</span>
          <span className='text-sm font-semibold md:text-base'>
            {feature.name}
          </span>
          {feature.tooltip && (
            <Popover>
              <PopoverTrigger>
                <HelpCircle className='h-4 w-4 text-black hover:text-gray-500' />
              </PopoverTrigger>
              <PopoverContent className='max-w-sm text-sm'>
                {feature.tooltip}
              </PopoverContent>
            </Popover>
          )}
        </div>
        {feature.description && (
          <div className='ml-7 text-xs opacity-50 md:ml-7 md:text-sm'>
            {feature.description}
          </div>
        )}
      </div>
      <div className='col-span-2 flex items-center justify-center md:col-span-3'>
        {feature.free ? (
          <Check className='h-4 w-4 text-black md:h-5 md:w-5' strokeWidth={3} />
        ) : (
          <XIcon
            className='h-4 w-4 text-black opacity-30 md:h-5 md:w-5'
            strokeWidth={3}
          />
        )}
      </div>
      <div className='col-span-2 flex items-center justify-center md:col-span-3'>
        {feature.pro ? (
          <CheckCheck
            className='h-4 w-4 text-green-500 md:h-5 md:w-5'
            strokeWidth={2.5}
          />
        ) : (
          <XIcon className='h-4 w-4 text-black opacity-50 md:h-5 md:w-5' />
        )}
      </div>
    </div>
  )

  return (
    <Elements stripe={stripePromise} options={options}>
      <div className='overflow-hidden rounded-xl bg-[#f0fdeb] text-black'>
        {/* Header */}
        <div className='grid grid-cols-12 gap-4 p-3 text-sm font-semibold md:p-4'>
          <div className='col-span-8 text-left md:col-span-6 md:text-lg'>
            {/* Features */}
          </div>
          <div className='col-span-2 text-center md:col-span-3 md:text-lg'>
            Free
          </div>
          <div className='col-span-2 text-center md:col-span-3 md:text-lg'>
            Pro
          </div>
        </div>

        {/* Feature rows */}
        <div className='px-0 md:px-4'>
          {features.map((feature, index) => (
            <FeatureRow key={index} feature={feature} />
          ))}
        </div>

        {/* Footer with pricing */}
        <div className='flex flex-col gap-4 py-2 md:py-6'>
          <div className='space-y-3 text-center md:space-y-6'>
            {/* Billing Toggle */}
            <div className='relative px-4 md:px-6'>
              <div className='absolute inset-y-0 left-1/2 border-r border-r-[#A9D9A0]/50' />
              <div className='relative grid grid-cols-12 items-center gap-1 rounded-lg border border-[#A9D9A0] py-2'>
                <div className='col-span-5'>
                  <Label
                    htmlFor='billing-toggle'
                    className={`cursor-pointer md:text-lg ${!isYearly ? 'text-black/90' : 'text-black/30'}`}
                  >
                    <div className='text-sm font-semibold md:text-base'>
                      Monthly
                    </div>
                    <div className='text-xs opacity-80'>
                      ${monthlyPriceInCents / 100}/mo
                    </div>
                    {applePaySupported && (
                      <div className='text-xs opacity-50'>7 day trial</div>
                    )}
                  </Label>
                </div>
                <div className='col-span-2'>
                  <Switch
                    id='billing-toggle'
                    checked={isYearly}
                    onCheckedChange={setIsYearly}
                    className='data-[state=checked]:bg-green-500'
                  />
                </div>
                <div className='col-span-5'>
                  <Label
                    htmlFor='billing-toggle'
                    className={`relative flex cursor-pointer flex-col md:text-lg ${isYearly ? 'text-black/90' : 'text-black/30'}`}
                  >
                    <div className='flex items-center justify-center gap-1'>
                      <span className='text-sm font-semibold md:text-base'>
                        Yearly
                      </span>
                      <span className='rounded-lg bg-[#D8F9D2] px-1.5 py-0.5 text-xs'>
                        {savings} off
                      </span>
                    </div>
                    <span className='inset-x-0 top-full text-xs opacity-60'>
                      ${(yearlyPriceInCents / 12 / 100).toFixed(2)}/mo
                      <br />
                      billed annually
                      {applePaySupported && (
                        <span className='text-xs opacity-50'>
                          <br />
                          30 day trial
                        </span>
                      )}
                    </span>
                  </Label>
                </div>
              </div>
            </div>

            {/* Subscribe Button */}
            <div className='space-y-2 px-4 md:px-6'>
              <div>
                <CheckoutButton
                  amount={AMOUNT_IN_CENTS} // amount in cents - needs to line up with the plan
                  planType={isYearly ? 'yearly' : 'monthly'}
                  processing={processing}
                  displayApplePayButton={(showing: boolean) => {
                    if (showing) {
                      setApplePaySupported(true)
                    } else {
                      setApplePaySupported(false)
                    }
                  }}
                  callback={(data) => {
                    handleSelectPlan('pro', data)
                    logger.debug('[Stripe] Payment callback', { data })
                  }}
                />

                {applePaySupported ? (
                  <>
                    <button
                      onClick={() => {
                        if (processing) return
                        handleSelectPlan('pro')
                      }}
                      className='mt-0 w-full py-2 text-xs font-semibold uppercase opacity-50'
                      disabled={processing}
                    >
                      More payment options
                    </button>

                    <div className='mx-auto flex w-1/2 items-center'>
                      <div className='flex-1 border-t border-gray-700' />
                      <div className='mx-4 text-xs text-gray-400'>or</div>
                      <div className='flex-1 border-t border-gray-700' />
                    </div>
                  </>
                ) : (
                  typeof applePaySupported === 'boolean' &&
                  !applePaySupported && (
                    <Button
                      onClick={() => handleSelectPlan('pro')}
                      disabled={processing}
                      fullWidth
                    >
                      {processing ? (
                        <div className='animate-spin'>
                          <LoaderIcon height={18} />
                        </div>
                      ) : (
                        <div className='flex flex-col'>
                          <span>
                            Subscribe for{' '}
                            {currencyFormatter(priceInCents / 100, {
                              currency: currency,
                              round: true,
                            })}
                          </span>
                          <span className='text-xs font-medium opacity-50'>
                            {freeTrialDays} day trial, cancel anytime
                          </span>
                        </div>
                      )}
                    </Button>
                  )
                )}
              </div>
              <div className='text-center'>
                {onCancel ? (
                  <Button
                    onClick={onCancel}
                    fullWidth
                    disabled={processing}
                    variant='custom'
                    hideBorder
                    size='md'
                    className='bg-transparent opacity-50'
                  >
                    Maybe Later
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSelectPlan('free')}
                    fullWidth
                    disabled={processing}
                    variant='custom'
                    hideBorder
                    size='md'
                    className='bg-transparent font-medium opacity-50'
                  >
                    Continue with basic search only
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Elements>
  )
}

export default PaywallContent
